import React, { useEffect, useRef, useState } from "react";

export const memberSeoTagSuggestions = [
  "Canadian Company",
  "Food/Drink",
  "Retail",
  "Personal Services",
  "Professional Services",
  "Marketing/Advertising",
  "Communications/Technology",
  "Automobile/Transportation",
  "Real Estate/Development",
  "Farming/Agriculture",
  "Financial",
  "Accommodations/Attractions",
  "Construction/Industrial",
  "Non-Profit",
  "Government/Education",
  "Health/Safety",
  "Sports/Recreation",
  "Indigenous-Owned Business",
  "Women-Owned Business",
  "Black-Owned Business",
  "2SLGBTQ+ Owned Business",
  "Pet-Friendly Establishment",
  "Self-Employed Business",
  "Small Business",
  "Franchise",
];

export type TagInputFieldProps = {
  id: string;
  value: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  dirty?: boolean;
  helperText?: React.ReactNode;
  helperFontSize?: 12 | 14;
  error?: string;
  suggestions?: string[];
};

export const TagInputField = ({
  id,
  value,
  onChange,
  disabled = false,
  dirty = false,
  helperText,
  helperFontSize = 14,
  error,
  suggestions = memberSeoTagSuggestions,
}: TagInputFieldProps) => {
  const [input, setInput] = useState("");
  const [validation, setValidation] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const valueKey = JSON.stringify(value);
  useEffect(() => {
    setInput("");
    setValidation("");
  }, [valueKey]);
  const add = (text: string) => {
    const tag = Array.from(text.replace(/<[^>]*>/g, ""))
      .filter(
        (character) =>
          character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127,
      )
      .join("")
      .trim();
    if (
      !tag ||
      value.some((item) => item.toLocaleLowerCase() === tag.toLocaleLowerCase())
    ) {
      setInput("");
      setValidation("");
      return;
    }
    if (tag.length > 60) {
      setValidation("Each tag can have up to 60 characters. Shorten it and try again.");
      return;
    }
    if (value.length >= 20) {
      setValidation("You can add up to 20 tags. Remove a tag to add a new one.");
      return;
    }
    onChange([...value, tag]);
    setInput("");
    setValidation("");
    inputRef.current?.focus();
  };
  const remove = (index: number) => {
    onChange(value.filter((_, itemIndex) => itemIndex !== index));
    setValidation("");
  };
  const visibleSuggestions = suggestions.filter(
    (suggestion) =>
      !value.some(
        (tag) => tag.toLocaleLowerCase() === suggestion.toLocaleLowerCase(),
      ),
  );
  const displayedError = error || validation;
  return (
    <div className="flex w-full max-w-[640px] flex-col gap-[10px]">
      <div
        data-member-field-error={Boolean(displayedError)}
        className={`flex flex-wrap items-center gap-[8px] rounded-[8px] border px-[10px] py-[8px] ${disabled ? "border-[#ECECEC] bg-[#F7F7F8]" : "border-[#9E9E9E] bg-white"}`}
        style={{
          ...(displayedError ? { borderColor: "#E24C4C" } : {}),
          ...(dirty && !displayedError ? { outline: "2px solid #E1A731", outlineOffset: -1 } : {}),
        }}
      >
        {value.map((tag, index) => (
          <span
            key={tag}
            className="inline-flex max-w-full items-center gap-[6px] rounded-full border border-[#ECECEC] bg-white py-[3px] pl-[10px] pr-[6px] text-[13px] font-semibold leading-[20px] text-[#0A0A0A]"
          >
            <span className="break-words">{tag}</span>
            <button
              type="button"
              title="Remove tag"
              aria-label={`Remove tag ${tag}`}
              disabled={disabled}
              onClick={() => remove(index)}
              className="flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full bg-[#F21B1B] text-white disabled:opacity-50"
            >
              <svg
                aria-hidden="true"
                width="8"
                height="8"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="3.4"
              >
                <path d="m2 2 8 8M10 2l-8 8" />
              </svg>
            </button>
          </span>
        ))}
        <input
          id={id}
          ref={inputRef}
          value={input}
          disabled={disabled}
          onChange={(event) => {
            setInput(event.target.value);
            setValidation("");
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === ",") {
              event.preventDefault();
              add(input);
            }
            if (event.key === "Backspace" && !input && value.length) {
              event.preventDefault();
              remove(value.length - 1);
            }
          }}
          aria-label="SEO and AIO tags"
          aria-invalid={Boolean(error || validation)}
          aria-describedby={
            error || validation || helperText ? `${id}-helper` : undefined
          }
          placeholder={
            value.length
              ? "Add another tag"
              : "e.g. Local Business, Retail, Family Owned"
          }
          className="min-w-[140px] flex-1 border-0 bg-transparent px-[2px] py-[5px] text-[14px] leading-[20px] text-[#0A0A0A] outline-none placeholder:text-[#9E9E9E]"
        />
      </div>
      {(error || validation || helperText) && (
        <div
          id={`${id}-helper`}
          role={error || validation ? "alert" : undefined}
          style={{ fontSize: helperFontSize }}
          className={error || validation ? "text-[#E24C4C]" : "text-[#727272]"}
        >
          {error || validation || helperText}
        </div>
      )}
      {visibleSuggestions.length > 0 && (
        <div className="flex flex-wrap items-center gap-[8px]">
          <span style={{ fontSize: helperFontSize }} className="text-[#727272]">
            Suggestions:
          </span>
          {visibleSuggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              disabled={disabled}
              onClick={() => add(suggestion)}
              className="rounded-[6px] border border-[#ECECEC] bg-[#F7F7F8] px-[10px] py-[5px] text-[12px] font-semibold text-[#0A0A0A] hover:border-[#E1A731] hover:bg-[#FDF8EE] disabled:opacity-50"
            >
              + {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagInputField;
